<template>
  <div class="h-full overflow-y-auto">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center">
        <img src="/hotel.svg" alt="Hotel" class="h-8 w-8 mr-3" />
        <h3 class="text-2xl font-bold text-gray-800">Nearby Hotels</h3>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mb-4"></div>
      <p class="text-gray-600">Finding nearby hotels...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex flex-col items-center justify-center h-64">
      <svg class="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <p class="text-gray-600 text-center mb-4">{{ error }}</p>
      <button 
        @click="$emit('retry')"
        class="bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-colors"
      >
        Try Again
      </button>
    </div>

    <!-- Content -->
    <div v-else-if="hotelsData && hotelsData.hotels" class="space-y-6">
      <!-- Stats Header -->
      <div class="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-4 border border-teal-200">
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-lg font-bold text-gray-800">{{ hotelsData.hotels.length }} Hotels Found</h4>
            <p class="text-gray-600 text-sm flex items-center">
              <svg class="h-4 w-4 mr-1 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              Near your location
            </p>
          </div>
          <div class="text-right">
            <div class="text-2xl">🏨</div>
          </div>
        </div>
      </div>

      <!-- Hotels List -->
      <div class="space-y-4">
        <div 
          v-for="(hotel, index) in hotelsData.hotels" 
          :key="hotel.id || index"
          class="bg-white rounded-xl transition-all duration-300 overflow-hidden border border-gray-200"
        >
          <!-- Hotel Header -->
          <div class="p-4">
            <div class="flex justify-between items-start mb-3">
              <div class="flex-1">
                <h3 class="font-bold text-lg text-gray-800 mb-1">{{ hotel.name }}</h3>
                <p v-if="hotel.address" class="text-gray-600 text-sm mb-2">{{ hotel.address }}</p>
                
                <!-- Rating and Distance -->
                <div class="flex items-center space-x-4 text-sm">
                  <div v-if="hotel.rating" class="flex items-center">
                    <span class="text-yellow-500 mr-1">⭐</span>
                    <span class="font-medium">{{ hotel.rating }}</span>
                  </div>
                  <div v-if="hotel.distance" class="text-gray-500">
                    📍 {{ formatDistance(hotel.distance) }}
                  </div>
                </div>
              </div>
              
              <!-- Hotel Icon -->
              <div class="ml-4">
                <div class="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <img src="/hotel.svg" alt="Hotel" class="h-6 w-6" />
                </div>
              </div>
            </div>

            <!-- Categories -->
            <div v-if="hotel.categories && hotel.categories.length > 0" class="mb-3">
              <div class="flex flex-wrap gap-2">
                <span 
                  v-for="category in hotel.categories.slice(0, 3)" 
                  :key="category"
                  class="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium"
                >
                  {{ formatCategory(category) }}
                </span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex flex-wrap gap-2">
              <button
                v-if="hotel.phone"
                @click="handleCall(hotel.phone)"
                class="flex items-center bg-teal-50 hover:bg-teal-100 text-teal-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call
              </button>

              <button
                v-if="hotel.url"
                @click="handleWebsite(hotel.url)"
                class="flex items-center bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Website
              </button>

              <button
                v-if="hotel.position && hotel.position.lat && hotel.position.lon"
                @click="handleDirections(hotel)"
                class="flex items-center bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Directions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else class="flex flex-col items-center justify-center h-64">
      <img src="/hotel.svg" alt="Hotel" class="h-16 w-16 opacity-50 mb-4" />
      <p class="text-gray-600 text-center">No hotels found nearby</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HotelsInline',
  props: {
    hotelsData: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  methods: {
    formatDistance(distance) {
      if (!distance) return '';
      if (distance < 1000) {
        return `${Math.round(distance)} m`;
      } else {
        return `${(distance / 1000).toFixed(1)} km`;
      }
    },
    formatCategory(category) {
      if (!category) return '';
      return category.replace(/[/_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    handleCall(phone) {
      if (phone) {
        window.open(`tel:${phone}`, '_self');
      }
    },
    handleWebsite(websiteUrl) {
      if (websiteUrl) {
        // Handle both full URLs and domain-only URLs
        const url = websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`;
        window.open(url, '_blank');
      }
    },
    handleDirections(hotel) {
      if (hotel.position && hotel.position.lat && hotel.position.lon) {
        this.$router.push({
          path: '/map',
          query: {
            lat: hotel.position.lat,
            lon: hotel.position.lon,
            name: hotel.name,
            address: hotel.address,
            showMarker: 'true'
          }
        });
      }
    }
  }
}
</script>
